import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  QaiButtonComponent,
  QaiDialogComponent,
  QaiFieldComponent,
  QaiMultiSelectComponent,
  QaiMultiSelectOption,
} from '@qai/ui';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ITeamAssociation } from 'src/app/interfaces/self-serve/assignment-manager/responses/iuser-assignment-summary';

@Component({
  selector: 'edit-team-associations-modal',
  standalone: true,
  templateUrl: './edit-team-associations-modal.component.html',
  styleUrls: ['./edit-team-associations-modal.component.scss'],
  imports: [
    QaiDialogComponent,
    QaiButtonComponent,
    QaiMultiSelectComponent,
    QaiFieldComponent,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class EditTeamAssociationsModalComponent {
  private readonly translocoService = inject(TranslocoService);

  readonly dialogRef = viewChild<QaiDialogComponent>('dialog');

  readonly open             = input<boolean>(false);
  readonly assignmentName   = input<string>('');
  readonly availableTeams   = input<ITeamAssociation[]>([]);
  readonly currentTeamUuids = input<string[]>([]);

  readonly closeModal  = output<void>();
  readonly teamsSaved  = output<string[]>();

  readonly i18nKeys = {
    modalHeading:         'simulationBuilder.EDIT_TEAM_ASSOCIATIONS_HEADING',
    descriptionKey:       'simulationBuilder.EDIT_TEAM_ASSOCIATIONS_DESCRIPTION',
    fieldLabelTeam:       'simulationBuilder.FIELD_LABEL_TEAM',
    searchTeamPlaceholder: 'simulationBuilder.SEARCH_TEAM_PLACEHOLDER',
    buttonCancel:         'simulationBuilder.BUTTON_CANCEL',
    buttonSave:           'simulationBuilder.BUTTON_SAVE_ASSOCIATIONS',
  };

  readonly selectedTeamUuids = signal<string[]>([]);
  readonly isSaving          = signal(false);

  readonly teamOptions = computed<QaiMultiSelectOption[]>(() =>
    this.availableTeams().map(t => ({ value: t.uuid, label: t.name }))
  );

  readonly canSave = computed(() => this.selectedTeamUuids().length > 0);

  readonly description = computed(() =>
    this.translocoService.translate(this.i18nKeys.descriptionKey, { assignmentName: this.assignmentName() })
  );

  constructor() {
    effect(() => {
      if (this.open()) {
        this.selectedTeamUuids.set([...this.currentTeamUuids()]);
        this.isSaving.set(false);
        this.dialogRef()?.showModal();
      } else {
        this.dialogRef()?.close();
      }
    });
  }

  onTeamSelectionChange(selectedValues: string[]): void {
    this.selectedTeamUuids.set(selectedValues);
  }

  onSave(): void {
    if (!this.canSave()) return;
    this.isSaving.set(true);
    this.teamsSaved.emit(this.selectedTeamUuids());
  }

  onCancel(): void {
    this.closeModal.emit();
  }
}
